"use client";

import { useState, useRef, useEffect } from "react";

const contactInfo = [
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
        <circle cx="12" cy="10" r="3"/>
      </svg>
    ),
    label: "Office Address",
    value: "507, Sterling Chambers, Poona Street, Dana Bunder, Mumbai – 400 009",
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.5a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.69h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 10.4a16 16 0 0 0 5.69 5.69l.88-.88a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.1 17.5l-.18-.58z"/>
      </svg>
    ),
    label: "Phone / Mobile",
    value: "+91 22 – 49642412 / +91 98211 77666",
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
        <polyline points="22,6 12,13 2,6"/>
      </svg>
    ),
    label: "Email",
    value: "info@blueseamarine.in",
  },
];

export default function ContactSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    service: "",
    port: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Connect to backend API endpoint
    console.log("Form submitted:", formData);
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 5000);
    setFormData({ name: "", email: "", company: "", phone: "", service: "", port: "", message: "" });
  };

  return (
    <section id="contact" ref={sectionRef} className="py-28 md:py-36 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-navy-800/40" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-ocean-700/8 blur-[120px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          className={`mb-16 ${visible ? "animate-fade-up" : "opacity-0"}`}
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-8 h-px bg-ocean-500" />
            <span className="text-[11px] uppercase tracking-[0.4em] text-ocean-400 font-bold">Get In Touch</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
            <span className="text-white">Request a</span>{" "}
            <span className="text-gradient-ocean italic">Marine Service</span>
          </h2>
          <p className="mt-4 text-[#8BA3BC] text-lg max-w-xl leading-relaxed">
            Contact us with your vessel's requirements and port of call. 
            Our team coordinates same-day response for urgent operations.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left: Contact info + compliance */}
          <div
            className={`lg:col-span-4 space-y-8 ${visible ? "animate-slide-left" : "opacity-0"}`}
            style={{ animationDelay: "0.2s" }}
          >
            {/* Contact cards */}
            {contactInfo.map((info, i) => (
              <div key={i} className="flex gap-4">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-ocean-400 flex-shrink-0"
                  style={{ background: "rgba(33,150,243,0.1)", border: "1px solid rgba(33,150,243,0.2)" }}>
                  {info.icon}
                </div>
                <div className="space-y-1">
                  <div className="text-[11px] uppercase tracking-widest text-[#8BA3BC] font-bold">{info.label}</div>
                  <div className="text-white font-medium text-[14px] leading-relaxed">{info.value}</div>
                </div>
              </div>
            ))}

            {/* Compliance card */}
            <div className="compliance-badge rounded-2xl p-6 space-y-3">
              <div className="text-gold-400 font-bold text-[13px] uppercase tracking-widest">Regulatory Compliance</div>
              <div className="space-y-2">
                {["MARPOL 73/78 Annex I, II, V", "IMO Circular MSC/Circ. 1432", "Port State Control Ready", "All Documentation Provided"].map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-gold-500 flex-shrink-0" />
                    <span className="text-[13px] text-[#8BA3BC]">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Operating hours */}
            <div className="glass-card rounded-2xl p-6 space-y-3">
              <div className="text-ocean-400 font-bold text-[13px] uppercase tracking-widest">Operating Hours</div>
              <div className="text-white font-bold text-lg">24 / 7 / 365</div>
              <p className="text-[#8BA3BC] text-[13px] leading-relaxed">
                Emergency coordination available round the clock for urgent vessel operations.
              </p>
            </div>
          </div>

          {/* Right: Form */}
          <div
            className={`lg:col-span-8 ${visible ? "animate-slide-right" : "opacity-0"}`}
            style={{ animationDelay: "0.3s" }}
          >
            <div className="glass-card rounded-3xl p-8 md:p-10">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-16 text-center space-y-4">
                  <div className="w-16 h-16 rounded-full bg-ocean-500/20 border border-ocean-500/30 flex items-center justify-center">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2196F3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-white font-display">Request Sent!</h3>
                  <p className="text-[#8BA3BC] max-w-sm">
                    Our team will respond within 2 hours. For urgent operations, call +91 98200 42412.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[11px] uppercase tracking-widest text-[#8BA3BC] font-bold block">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Capt. John Smith"
                        className="form-input w-full rounded-xl px-5 py-4 text-[14px]"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[11px] uppercase tracking-widest text-[#8BA3BC] font-bold block">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="captain@vessel.com"
                        className="form-input w-full rounded-xl px-5 py-4 text-[14px]"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[11px] uppercase tracking-widest text-[#8BA3BC] font-bold block">Company / Vessel</label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="MV Pacific Star / Shipping Co."
                        className="form-input w-full rounded-xl px-5 py-4 text-[14px]"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[11px] uppercase tracking-widest text-[#8BA3BC] font-bold block">Phone / Mobile</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 98200 00000"
                        className="form-input w-full rounded-xl px-5 py-4 text-[14px]"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[11px] uppercase tracking-widest text-[#8BA3BC] font-bold block">Service Required *</label>
                      <select
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        required
                        className="form-input w-full rounded-xl px-5 py-4 text-[14px]"
                      >
                        <option value="" disabled>Select service type</option>
                        <option value="waste">Waste Removal (Sludge/Slop/Bilge)</option>
                        <option value="tank">Tank Cleaning</option>
                        <option value="supply">Supply Services (Stores/Provisions)</option>
                        <option value="bunker">Bunker Supply</option>
                        <option value="survey">Marine Survey</option>
                        <option value="other">Other / Multiple Services</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[11px] uppercase tracking-widest text-[#8BA3BC] font-bold block">Port of Call *</label>
                      <input
                        type="text"
                        name="port"
                        value={formData.port}
                        onChange={handleChange}
                        required
                        placeholder="e.g. Nhava Sheva, Jebel Ali"
                        className="form-input w-full rounded-xl px-5 py-4 text-[14px]"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[11px] uppercase tracking-widest text-[#8BA3BC] font-bold block">Message / Requirements</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Describe your vessel's requirements, ETA, quantity estimates..."
                      className="form-input w-full rounded-xl px-5 py-4 text-[14px] resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full md:w-auto inline-flex items-center justify-center gap-3 px-10 py-5 rounded-full bg-ocean-600 hover:bg-ocean-500 text-white font-bold text-sm uppercase tracking-widest transition-all duration-300 shadow-ocean hover:shadow-glow-blue hover:-translate-y-0.5"
                  >
                    Send Service Request
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4 20-7z"/>
                    </svg>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}